import React, { useEffect, useState } from "react";
import {
  Input,
  Button,
  Form,
  message,
  Popconfirm,
  TimePicker,
  InputNumber,
  Select,
  Tag,
} from "antd";
import {
  DashboardOutlined,
  AimOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CarOutlined,
  HistoryOutlined,
  FieldTimeOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import "./style.css";
import dayjs from "dayjs";
import delIcon from "../../../Assets/Icons/deleteTable.svg";
import viewIcon from "../../../Assets/Icons/view.svg";
import GenericTable from "../../../Components/GenericTable";
import GenericModal from "../../../Components/GenericModal";
import {
  addTheftArea,
  deleteTheftArea,
  editHighTheftAreaRadius,
  editHighTheftPeriod,
  editSpeedLimit,
  syncGeofence,
  editSafeSpeed,
  getSystemConfigurations,
  editHarshDriving,
  editRiskRetention,
  editDetectionTime,
  editAlertResetTime,
  editMultipleIgnitionOnOffThreshold,
  editIgnitionGatedFactors,
} from "../../../API/Admin/sytemConfiguration";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Circle,
  useMapEvents,
  useMap,
} from "react-leaflet";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { ClipLoader } from "react-spinners";

const MapUpdater = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    if (lat && lng) {
      map.setView([parseFloat(lat), parseFloat(lng)], 15);
    }
  }, [lat, lng, map]);

  return null;
};

const MapPreviewUpdater = ({ lat, lng }) => {
  const map = useMap();

  useEffect(() => {
    if (lat === undefined || lat === null || lng === undefined || lng === null) {
      return;
    }

    const timer = setTimeout(() => {
      map.invalidateSize();
      map.setView([parseFloat(lat), parseFloat(lng)], 14);
    }, 200);

    return () => clearTimeout(timer);
  }, [lat, lng, map]);

  return null;
};

const FitAllBounds = ({ points, radiusKm }) => {
  const map = useMap();

  useEffect(() => {
    if (!points || points.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      map.invalidateSize();

      const radiusMeters = radiusKm ? parseFloat(radiusKm) * 1000 : 0;
      const bounds = L.latLngBounds([]);

      points.forEach((point) => {
        const lat = parseFloat(point.latitude);
        const lng = parseFloat(point.longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        if (radiusMeters > 0) {
          const degLat = radiusMeters / 111320;
          const degLng =
            radiusMeters / (111320 * Math.cos((lat * Math.PI) / 180));

          bounds.extend([lat + degLat, lng + degLng]);
          bounds.extend([lat - degLat, lng - degLng]);
        } else {
          bounds.extend([lat, lng]);
        }
      });

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [points, radiusKm, map]);

  return null;
};

L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const SearchControl = ({ setCoords }) => {
  const map = useMap();
  useEffect(() => {
    const provider = new OpenStreetMapProvider();
    const searchControl = new GeoSearchControl({
      provider,
      style: "bar",
      showMarker: false,
      autoClose: true,
    });
    map.addControl(searchControl);
    map.on("geosearch/showlocation", (result) => {
      const { x, y } = result.location;
      setCoords({ lat: y, lng: x });
      map.setView([y, x], 15);
    });
    return () => map.removeControl(searchControl);
  }, [map, setCoords]);
  return null;
};

const ClickableMap = ({ setCoords }) => {
  useMapEvents({
    click: (e) => {
      setCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

const ConfigurationSettings = () => {
  const [speedForm] = Form.useForm();
  const [safeSpeedForm] = Form.useForm();
  const [radiusForm] = Form.useForm();
  const [periodForm] = Form.useForm();
  const [detectionForm] = Form.useForm();
  const [alertResetTimeForm] = Form.useForm();
  const [multipleIgnitionThresholdForm] = Form.useForm();
  const [harshDrivingForm] = Form.useForm();
  const [riskRetentionForm] = Form.useForm();
  const [ignitionGatedFactorsForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [detectionFactors, setDetectionFactors] = useState([]);
  const [currentLng, setCurrentLng] = useState("");
  const [currentLat, setCurrentLat] = useState("");
  const [areaRadiusKm, setAreaRadiusKm] = useState(null);
  const [isMapPreviewOpen, setIsMapPreviewOpen] = useState(false);
  const [previewPoint, setPreviewPoint] = useState(null);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [loadingSystemConfigurations, setLoadingSystemConfigurations] =
    useState(false);
  const [factors, setFactors] = useState([]);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    try {
      setLoadingSystemConfigurations(true);
      const res = await getSystemConfigurations();

      const config = res.data.data.data;
      const factors = res.data.data.factors || [];
      const detectionFactors = res.data.data.detectionFactors || [];
      setFactors(factors);
      setDetectionFactors(detectionFactors);
      
      // SPEED LIMIT
      if (config?.speed_limit) {
        speedForm.setFieldsValue({
          speed_limit: config.speed_limit.max_speed,
        });
      }

      // SAFE SPEED
      if (config?.safe_speed) {
        safeSpeedForm.setFieldsValue({
          safe_speed: config.safe_speed.safe_speed,
        });
      }

      // AREA RADIUS
      if (config?.high_theft_area_radius) {
        radiusForm.setFieldsValue({
          area_radius: config.high_theft_area_radius.area_radius,
        });
        setAreaRadiusKm(config.high_theft_area_radius.area_radius);
      }

      // PERIOD
      if (config?.high_theft_period) {
        periodForm.setFieldsValue({
          start_time: dayjs(config.high_theft_period.start_time, "HH:mm:ss"),
          end_time: dayjs(config.high_theft_period.end_time, "HH:mm:ss"),
        });
      }

      // HIGH THEFT AREA POINTS
      if (config?.high_theft_area) {
        const points = config.high_theft_area.points.map((point, index) => ({
          key: index + 1,
          longitude: point.lng,
          latitude: point.lat,
        }));

        setDataSource(points);
      }

      // DETECTION TIME WINDOW
      if (config?.detection_window_minutes) {
        detectionForm.setFieldsValue({
          detection_window_minutes:
            config.detection_window_minutes.detection_window_minutes,
        });
      }

      // ALERT REST TIME
      if (config?.alert_reset_time) {
        alertResetTimeForm.setFieldsValue({
          alert_reset_minutes:
            config.alert_reset_time.alert_reset_minutes,
        });
      }

      // MULTIPLE IGNITION ON/OFF THRESHOLD
      if (config?.multiple_ignition_on_off_threshold) {
        multipleIgnitionThresholdForm.setFieldsValue({
          multiple_ignition_on_off_threshold:
            config.multiple_ignition_on_off_threshold.multiple_ignition_on_off_threshold,
        });
      }

      // HARSH DRIVING
      // if (config?.harsh_driving_config) {
      //   harshDrivingForm.setFieldsValue({
      //     speed_delta_threshold:
      //       config.harsh_driving_config.speed_delta_threshold,
      //     speed_time_window: config.harsh_driving_config.speed_time_window,
      //   });
      // }

      // RISK FACTOR RETENTION
      if (config?.risk_factor_retention) {
        riskRetentionForm.setFieldsValue({
          retention_time: config.risk_factor_retention.retention_time,
          factors: config.risk_factor_retention.factors,
        });
      }

      // IGNITION GATED FACTORS
      if (config?.ignition_gated_factors) {
        ignitionGatedFactorsForm.setFieldsValue({
          factors: config.ignition_gated_factors.factors,
        });
      }
    } catch (error) {
      messageApi.error("Failed to load configuration");
    } finally {
      setLoadingSystemConfigurations(false);
    }
  };

  const handleAddCoordinate = async () => {
    if (!currentLat || !currentLng) {
      return messageApi.warning("Please select a location on the map first");
    }

    const newPoint = { lat: currentLat, lng: currentLng };

    try {
      const res = await addTheftArea([newPoint], "append");

      messageApi.success(res.data.message || "Location added successfully");

      const newEntry = {
        key: dataSource.length + 1,
        longitude: currentLng,
        latitude: currentLat,
      };
      setDataSource((prev) => [...prev, newEntry]);

      setCurrentLat("");
      setCurrentLng("");
    } catch (err) {
      messageApi.error(err.response?.data?.message || "Failed to add location");
    }
  };

  const handleDelete = async (key) => {
    const pointToDelete = dataSource.find((item) => item.key === key);

    if (!pointToDelete) return;

    const points = [
      {
        lat: pointToDelete.latitude,
        lng: pointToDelete.longitude,
      },
    ];

    try {
      const res = await deleteTheftArea(points, "delete");

      messageApi.success(res.data.message || "Location removed successfully");

      setDataSource((prev) => prev.filter((item) => item.key !== key));
    } catch (err) {
      messageApi.error(
        err.response?.data?.message || "Failed to delete location",
      );
    }
  };

  const handleViewOnMap = (record) => {
    setPreviewPoint(record);
    setIsMapPreviewOpen(true);
  };

  const handleCloseMapPreview = () => {
    setIsMapPreviewOpen(false);
    setPreviewPoint(null);
  };

  const mapColumns = [
    { title: "Longitude", dataIndex: "longitude", key: "longitude" },
    { title: "Latitude", dataIndex: "latitude", key: "latitude" },
    {
      title: "View",
      key: "view",
      render: (_, record) => (
        <img
          src={viewIcon}
          alt="View on map"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewOnMap(record)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Are sure to delete this?"
          onConfirm={() => handleDelete(record.key)}
        >
          <img src={delIcon} alt="Delete" style={{ cursor: "pointer" }} />
        </Popconfirm>
      ),
    },
  ];

  const updateFromMap = ({ lat, lng }) => {
    const coordString = `${lat.toFixed(6)},${lng.toFixed(6)}`;

    const [latitude, longitude] = coordString.split(",");

    setCurrentLat(latitude);
    setCurrentLng(longitude);
  };

  const handleSaveSpeedLimit = async () => {
    try {
      const values = await speedForm.validateFields();
      const name = "default_speed";
      const max_speed = parseFloat(values.speed_limit);

      const res = await editSpeedLimit(name, max_speed);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(
          res.data.message || "Speed limit updated successfully",
        );
      } else {
        messageApi.error(res.data.message || "Failed to update speed limit");
      }
    } catch (err) {
      if (err.response && err.response.data?.message) {
        messageApi.error(err.response.data.message);
      } else {
        messageApi.error("Please enter a valid speed limit");
      }
    }
  };

  const handleSaveSafeSpeed = async () => {
    try {
      const values = await safeSpeedForm.validateFields();
      const name = "safe_speed";
      const safe_speed = parseFloat(values.safe_speed);

      const res = await editSafeSpeed(name, safe_speed);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(
          res.data.message || "Safe Speed updated successfully",
        );
      } else {
        messageApi.error(res.data.message || "Failed to update safe speed");
      }
    } catch (err) {
      if (err.response && err.response.data?.message) {
        messageApi.error(err.response.data.message);
      } else {
        messageApi.error("Please enter a valid safe speed");
      }
    }
  };

  // const handleSaveHarshDriving = async () => {
  //   try {
  //     const values = await harshDrivingForm.validateFields();

  //     const speed_delta_threshold = parseFloat(values.speed_delta_threshold);
  //     const speed_time_window = parseInt(values.speed_time_window);

  //     const res = await editHarshDriving(
  //       speed_delta_threshold,
  //       speed_time_window,
  //     );

  //     if (Math.floor(res.status / 100) === 2) {
  //       messageApi.success(
  //         res.data.message || "Harsh driving config updated successfully",
  //       );
  //     } else {
  //       messageApi.error(
  //         res.data.message || "Failed to update harsh driving config",
  //       );
  //     }
  //   } catch (err) {
  //     if (err.response?.data?.message) {
  //       messageApi.error(err.response.data.message);
  //     } else {
  //       messageApi.error("Please enter valid harsh driving values");
  //     }
  //   }
  // };

  const handleSaveDetectionTimeWindow = async () => {
    try {
      const values = await detectionForm.validateFields();

      const detection_window_minutes = Number(values.detection_window_minutes);

      if (isNaN(detection_window_minutes)) {
        messageApi.error("Please enter a valid number");
        return;
      }

      const res = await editDetectionTime(detection_window_minutes);

      messageApi.success(
        res?.data?.message || "Detection time window updated successfully",
      );
    } catch (err) {
      const errorData = err?.response?.data;

      if (errorData && typeof errorData === "object") {
        const firstKey = Object.keys(errorData)[0];
        const firstMessage = errorData[firstKey]?.[0];

        if (firstMessage) {
          messageApi.error(firstMessage);
          return;
        }
      }

      messageApi.error(
        err?.response?.data?.message ||
        "Failed to update detection time window",
      );
    }
  };

  const handleSaveAlertResetTime = async () => {
    try {
      const values = await alertResetTimeForm.validateFields();

      const alert_reset_minutes = Number(values.alert_reset_minutes);

      if (isNaN(alert_reset_minutes)) {
        messageApi.error("Please enter a valid number");
        return;
      }

      const res = await editAlertResetTime(alert_reset_minutes);

      messageApi.success(
        res?.data?.message || "Alert Reset time window updated successfully",
      );
    } catch (err) {
      const errorData = err?.response?.data;

      if (errorData && typeof errorData === "object") {
        const firstKey = Object.keys(errorData)[0];
        const firstMessage = errorData[firstKey]?.[0];

        if (firstMessage) {
          messageApi.error(firstMessage);
          return;
        }
      }

      messageApi.error(
        err?.response?.data?.message ||
        "Failed to update alert reset time window",
      );
    }
  };

  const handleSaveMultipleIgnitionThreshold = async () => {
    try {
      const values = await multipleIgnitionThresholdForm.validateFields();

      const multiple_ignition_on_off_threshold = Number(
        values.multiple_ignition_on_off_threshold,
      );

      if (isNaN(multiple_ignition_on_off_threshold)) {
        messageApi.error("Please enter a valid number");
        return;
      }

      const res = await editMultipleIgnitionOnOffThreshold(
        multiple_ignition_on_off_threshold,
      );

      messageApi.success(
        res?.data?.message ||
        "Multiple ignition on/off threshold updated successfully",
      );
    } catch (err) {
      const errorData = err?.response?.data;

      if (errorData && typeof errorData === "object") {
        const firstKey = Object.keys(errorData)[0];
        const firstMessage = errorData[firstKey]?.[0];

        if (firstMessage) {
          messageApi.error(firstMessage);
          return;
        }
      }

      messageApi.error(
        err?.response?.data?.message ||
        "Failed to update multiple ignition on/off threshold",
      );
    }
  };

  const handleSaveHighTheftAreaRadius = async () => {
    try {
      const values = await radiusForm.validateFields();
      const area_radius = parseFloat(values.area_radius);

      const res = await editHighTheftAreaRadius(area_radius);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(res.data.message || "Radius updated successfully");
        setAreaRadiusKm(area_radius);
      } else {
        messageApi.error(res.data.message || "Failed to update radius");
      }
    } catch (err) {
      if (err.response && err.response.data?.message) {
        messageApi.error(err.response.data.message);
      } else {
        messageApi.error("Please enter a valid area radius");
      }
    }
  };

  const handleSaveHighTheftPeriod = async () => {
    try {
      const values = await periodForm.validateFields();

      const payload = {
        name: "high_theft_period",
        start_time: values.start_time.format("HH:mm:ss"),
        end_time: values.end_time.format("HH:mm:ss"),
      };

      const res = await editHighTheftPeriod(payload);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(
          res.data.message || "High theft period updated successfully",
        );
      } else {
        messageApi.error(
          res.data.message || "Failed to update high theft period",
        );
      }
    } catch (err) {
      if (err.response && err.response.data?.message) {
        messageApi.error(err.response.data.message);
      } else {
        messageApi.error("Please enter valid start and end times");
      }
    }
  };

  const handleSaveRiskRetention = async () => {
    try {
      const values = await riskRetentionForm.validateFields();

      const retention_time = values.retention_time;
      const factors = values.factors || [];

      const res = await editRiskRetention(retention_time, factors);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(
          res.data.message || "Risk retention updated successfully",
        );
      } else {
        messageApi.error(res.data.message || "Failed to update risk retention");
      }
    } catch (err) {
      messageApi.error(
        err.response?.data?.message ||
        "Please provide valid retention settings",
      );
    }
  };

  const handleSaveIgnitionGatedFactors = async () => {
    try {
      const values = await ignitionGatedFactorsForm.validateFields();

      const factors = values.factors || [];

      const res = await editIgnitionGatedFactors(factors);

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(
          res.data.message || "Ignition gated factors updated successfully",
        );
      } else {
        messageApi.error(
          res.data.message || "Failed to update ignition gated factors",
        );
      }
    } catch (err) {
      messageApi.error(
        err.response?.data?.message ||
        "Please select valid factors",
      );
    }
  };

  const handleSyncGeofence = async () => {
    try {
      setLoading(true);
      const res = await syncGeofence();

      if (Math.floor(res.status / 100) === 2) {
        messageApi.success(res.data.message || "Geofences synced successfully");
      } else {
        messageApi.error(res.data.message || "Failed to sync geofences");
      }
    } catch (err) {
      messageApi.error(
        err.response?.data?.message || "Error syncing geofences",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedFactors = Form.useWatch("factors", riskRetentionForm) || [];
  const selectedIgnitionGatedFactors =
    Form.useWatch("factors", ignitionGatedFactorsForm) || [];

  return (
    <div className="config-container">
      {contextHolder}
      {loadingSystemConfigurations && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <ClipLoader size={50} color={"#123abc"} loading={true} />
        </div>
      )}
      <div className="config-row">
        {/* Speed Limit */}
        <div className="config-card">
          <div className="top-info">
            <div className="card-header">
              <div className="icon-wrapper">
                <DashboardOutlined />
              </div>
              <div className="header-text">
                <h3>Vehicle Speed & Area Radius Configuration</h3>
                <p>Configure thresholds for automatic speed alerts.</p>
              </div>
            </div>
            <Form layout="vertical" form={speedForm}>
              <div className="input-label">Speed Limit (km/h)</div>
              <Form.Item name="speed_limit">
                <Input
                  type="number"
                  placeholder="Enter Speed Limit"
                  suffix="KM/H"
                  size="large"
                />
              </Form.Item>
            </Form>
            <p className="helper-text">
              Vehicles exceeding this speed will trigger alerts and risk
              scoring.
            </p>
          </div>
          <div className="save-btn-container">
            <Button
              className="custom-action-btn"
              onClick={handleSaveSpeedLimit}
            >
              Save
            </Button>
          </div>
        </div>

        {/* High Theft Area Radius */}
        <div className="config-card">
          <div className="top-info">
            <div className="card-header">
              <div className="icon-wrapper">
                <AimOutlined />
              </div>
              <div className="header-text">
                <h3>High Theft Area Radius Configuration</h3>
                <p>
                  Configure the radius of the high-risk theft zone for
                  area-based Management.
                </p>
              </div>
            </div>
            <Form layout="vertical" form={radiusForm}>
              <div className="input-label">Area Radius (Km)</div>
              <Form.Item name="area_radius">
                <Input
                  type="number"
                  placeholder="Enter Area Radius"
                  suffix="km"
                  size="large"
                />
              </Form.Item>
            </Form>
            <p className="helper-text">
              Vehicles entering or operating within this radius will be
              monitored as high-risk and may trigger alerts and risk scoring.
            </p>
          </div>
          <div className="save-btn-container">
            <Button
              className="custom-action-btn"
              onClick={handleSaveHighTheftAreaRadius}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Safe Speed */}
      <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper">
              <DashboardOutlined />
            </div>
            <div className="header-text">
              <h3>Vehicle Safe Speed</h3>
              <p>Configure speed at which vehicle an be shut down.</p>
            </div>
          </div>
          <Form layout="vertical" form={safeSpeedForm}>
            <div className="input-label">Safe Speed (km/h)</div>
            <Form.Item name="safe_speed">
              <Input
                type="number"
                placeholder="Enter Safe Speed"
                suffix="KM/H"
                size="large"
              />
            </Form.Item>
          </Form>
          <p className="helper-text">
            Vehicles below this speed will trigger shut down.
          </p>
        </div>
        <div className="save-btn-container">
          <Button className="custom-action-btn" onClick={handleSaveSafeSpeed}>
            Save
          </Button>
        </div>
      </div>

      {/* High Theft Area Points */}
      <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper">
              <EnvironmentOutlined />
            </div>
            <div className="header-text">
              <h3>High Theft Area Settings</h3>
              <p>Defines the coordinates for the high-risk theft zone</p>
            </div>
          </div>

          <div
            className="config-row"
            style={{ gap: "16px", marginBottom: "20px" }}
          >
            <div style={{ flex: 1 }}>
              <div className="input-label">Longitude</div>
              <Input
                type="number"
                placeholder="Longitude"
                value={currentLng}
                size="large"
                onChange={(e) => setCurrentLng(e.target.value)}
              />
            </div>

            <div style={{ flex: 1 }}>
              <div className="input-label">Latitude</div>
              <Input
                type="number"
                placeholder="Latitude"
                value={currentLat}
                size="large"
                onChange={(e) => setCurrentLat(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                onClick={handleAddCoordinate}
                className="custom-action-btn"
              >
                Add
              </Button>
            </div>
          </div>

          <div
            className="map-container-wrapper"
            style={{
              height: "300px",
              marginBottom: "20px",
              borderRadius: "8px",
              overflow: "hidden",
              border: "1px solid #d9d9d9",
            }}
          >
            <MapContainer
              center={[18.1096, -77.2975]}
              zoom={6}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              <MapUpdater lat={currentLat} lng={currentLng} />

              {currentLat && currentLng && (
                <Marker
                  position={[parseFloat(currentLat), parseFloat(currentLng)]}
                >
                  <Popup>Current Selection</Popup>
                </Marker>
              )}

              <ClickableMap setCoords={updateFromMap} />
              <SearchControl setCoords={updateFromMap} />
            </MapContainer>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "12px",
            }}
          >
            <Button
              className="custom-action-btn"
              disabled={dataSource.length === 0}
              onClick={() => setIsViewAllOpen(true)}
            >
              View All Areas
            </Button>
          </div>

          <div className="coordinates-table-wrapper">
            <GenericTable
              columns={mapColumns}
              dataSource={dataSource}
              className="custom-table"
            />
          </div>

          <GenericModal
            title="All High Theft Areas"
            open={isViewAllOpen}
            onCancel={() => setIsViewAllOpen(false)}
            footer={null}
          >
            {isViewAllOpen && (
              <div style={{ height: "500px", width: "100%" }}>
                <MapContainer
                  center={[18.1096, -77.2975]}
                  zoom={6}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <FitAllBounds points={dataSource} radiusKm={areaRadiusKm} />

                  {dataSource.map((point) => {
                    const lat = parseFloat(point.latitude);
                    const lng = parseFloat(point.longitude);

                    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

                    return (
                      <React.Fragment key={point.key}>
                        <Marker position={[lat, lng]}>
                          <Popup>{`Lat: ${lat}, Lng: ${lng}`}</Popup>
                        </Marker>
                        {!!areaRadiusKm && parseFloat(areaRadiusKm) > 0 && (
                          <Circle
                            center={[lat, lng]}
                            radius={parseFloat(areaRadiusKm) * 1000}
                            pathOptions={{
                              color: "red",
                              fillColor: "red",
                              fillOpacity: 0.2,
                              weight: 2,
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </MapContainer>
              </div>
            )}
          </GenericModal>

          <GenericModal
            title="High Theft Area"
            open={isMapPreviewOpen}
            onCancel={handleCloseMapPreview}
            footer={null}
          >
            {previewPoint && isMapPreviewOpen && (
              <div style={{ height: "400px", width: "100%" }}>
                <MapContainer
                  key={previewPoint.key}
                  center={[
                    parseFloat(previewPoint.latitude),
                    parseFloat(previewPoint.longitude),
                  ]}
                  zoom={14}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapPreviewUpdater
                    lat={previewPoint.latitude}
                    lng={previewPoint.longitude}
                  />
                  <Marker
                    position={[
                      parseFloat(previewPoint.latitude),
                      parseFloat(previewPoint.longitude),
                    ]}
                  >
                    <Popup>High Theft Area</Popup>
                  </Marker>
                  {!!areaRadiusKm && parseFloat(areaRadiusKm) > 0 && (
                    <Circle
                      center={[
                        parseFloat(previewPoint.latitude),
                        parseFloat(previewPoint.longitude),
                      ]}
                      radius={parseFloat(areaRadiusKm) * 1000}
                      pathOptions={{
                        color: "red",
                        fillColor: "red",
                        fillOpacity: 0.2,
                        weight: 2,
                      }}
                    />
                  )}
                </MapContainer>
              </div>
            )}
          </GenericModal>
        </div>
      </div>

      {/* High Theft Time */}
      <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper" style={{ background: "#FFFBEB" }}>
              <ClockCircleOutlined style={{ color: "#FFBF00" }} />
            </div>
            <div className="header-text">
              <h3>High Theft Period (Jamaican Time)</h3>
              <p>Define periods where unauthorized movement is critical</p>
            </div>
          </div>

          <Form form={periodForm} layout="vertical">
            <div className="config-row" style={{ gap: "24px" }}>
              <div style={{ flex: 1 }}>
                <div className="input-label">Start Time</div>
                <Form.Item name="start_time">
                  <TimePicker
                    format="hh:mm A"
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>

              <div style={{ flex: 1 }}>
                <div className="input-label">End Time</div>
                <Form.Item name="end_time">
                  <TimePicker
                    format="hh:mm A"
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>

          <div className="info-alert-box">
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <AimOutlined />
              <span>
                Activity during these hours will be treated as high-risk.
              </span>
            </div>
          </div>
        </div>

        <div className="save-btn-container">
          <Button
            className="custom-action-btn "
            onClick={handleSaveHighTheftPeriod}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Harsh Driving */}
      {/* <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper" style={{ background: "#FFFBEB" }}>
              <CarOutlined style={{ color: "#FFBF00" }} />
            </div>
            <div className="header-text">
              <h3>Harsh Driving Configuration</h3>
              <p>
                Set speed variation limits and monitoring time window to detect
                unsafe or aggressive driving behavior.
              </p>
            </div>
          </div>

          <Form form={harshDrivingForm} layout="vertical">
            <div className="config-row" style={{ gap: "24px" }}>
              <div style={{ flex: 1 }}>
                <div className="input-label">Speed Variation Threshold</div>
                <Form.Item name="speed_delta_threshold">
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    min={0}
                    suffix="KM/H"
                    placeholder="Enter speed variation"
                  />
                </Form.Item>
              </div>

              <div style={{ flex: 1 }}>
                <div className="input-label">Monitoring Time Window</div>
                <Form.Item name="speed_time_window">
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    min={1}
                    suffix="secs"
                    placeholder="Enter monitoring time in seconds"
                  />
                </Form.Item>
              </div>
            </div>
          </Form>
        </div>

        <div className="save-btn-container">
          <Button
            className="custom-action-btn "
            onClick={handleSaveHarshDriving}
          >
            Save
          </Button>
        </div>
      </div> */}

      <div className="config-row">
        {/* Detection Time Window */}
        <div className="config-card">
          <div className="top-info">
            <div className="card-header">
              <div className="icon-wrapper" style={{ background: "#E6F7FF" }}>
                <FieldTimeOutlined style={{ color: "#1677FF" }} />
              </div>
              <div className="header-text">
                <h3>Detection Time Window</h3>
                <p>
                  Configure the time window used by the system to evaluate and
                  detect vehicle activity patterns ({detectionFactors.join(", ")}).
                </p>
              </div>
            </div>

            <Form form={detectionForm} layout="vertical">
              <div className="input-label">Detection Time Window</div>

              <Form.Item
                name="detection_window_minutes"
                rules={[
                  {
                    required: true,
                    message: "Please enter detection time window",
                  },
                ]}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                  suffix="mins"
                  placeholder="Enter detection time window in minutes"
                />
              </Form.Item>
            </Form>
          </div>

          <div className="save-btn-container">
            <Button
              className="custom-action-btn"
              onClick={handleSaveDetectionTimeWindow}
            >
              Save
            </Button>
          </div>
        </div>
        {/* Alert Reset Time */}
        <div className="config-card">
          <div className="top-info">
            <div className="card-header">
              <div className="icon-wrapper" style={{ background: "#FFF7E6" }}>
                <FieldTimeOutlined style={{ color: "#FA8C16" }} />
              </div>

              <div className="header-text">
                <h3>Alert Reset Time</h3>
                <p>
                  Configure how long an alert remains active before the system resets it and starts evaluating theft factors again.
                </p>
              </div>
            </div>

            <Form form={alertResetTimeForm} layout="vertical">
              <div className="input-label">Alert Reset Time</div>

              <Form.Item
                name="alert_reset_minutes"
                rules={[
                  {
                    required: true,
                    message: "Please enter alert reset time",
                  },
                ]}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                  suffix="mins"
                  placeholder="Enter alert reset time in minutes"
                />
              </Form.Item>
            </Form>
          </div>

          <div className="save-btn-container">
            <Button
              className="custom-action-btn"
              onClick={handleSaveAlertResetTime}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Multiple Ignition On/Off Threshold */}
        <div className="config-card">
          <div className="top-info">
            <div className="card-header">
              <div className="icon-wrapper" style={{ background: "#F0F5FF" }}>
                <CarOutlined style={{ color: "#2F54EB" }} />
              </div>

              <div className="header-text">
                <h3>Multiple Ignition On/Off Threshold</h3>
                <p>
                  Configure how many ignition on/off events within the
                  detection time window are flagged as a risk factor.
                </p>
              </div>
            </div>

            <Form form={multipleIgnitionThresholdForm} layout="vertical">
              <div className="input-label">Multiple Ignition On/Off Threshold</div>

              <Form.Item
                name="multiple_ignition_on_off_threshold"
                rules={[
                  {
                    required: true,
                    message: "Please enter the ignition on/off threshold",
                  },
                ]}
              >
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  min={1}
                  suffix="times"
                  placeholder="Enter ignition on/off threshold"
                />
              </Form.Item>
            </Form>
          </div>

          <div className="save-btn-container">
            <Button
              className="custom-action-btn"
              onClick={handleSaveMultipleIgnitionThreshold}
            >
              Save
            </Button>
          </div>
        </div>
      </div>


      {/* Risk Factor Retention */}
      <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper" style={{ background: "#FFFBEB" }}>
              <HistoryOutlined style={{ color: "#FFBF00" }} />
            </div>
            <div className="header-text">
              <h3>Risk Factor Retention Configuration</h3>
              <p>
                Configure how long selected risk factors remain active in a
                vehicle's risk score before they automatically expire.
              </p>
            </div>
          </div>

          <Form form={riskRetentionForm} layout="vertical">
            <div className="config-row" style={{ gap: "24px" }}>
              <div style={{ flex: 1 }}>
                <div className="input-label">Risk Retention Time</div>
                <Form.Item name="retention_time">
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    min={0}
                    suffix="mins"
                    placeholder="Enter Risk Retention Time in mins"
                  />
                </Form.Item>
              </div>

              <div style={{ flex: 1 }}>
                <div className="input-label">Factors</div>
                <Form.Item
                  name="factors"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one factor",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    size="large"
                    placeholder="Select Risk Factors"
                    options={factors.map((factor) => ({
                      label: factor,
                      value: factor,
                    }))}
                  />
                </Form.Item>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  {selectedFactors.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {selectedFactors.map((factor) => (
                        <Tag
                          key={factor}
                          closable
                          onClose={() => {
                            riskRetentionForm.setFieldValue(
                              "factors",
                              selectedFactors.filter((f) => f !== factor),
                            );
                          }}
                        >
                          {factor}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </div>

        <div className="save-btn-container">
          <Button
            className="custom-action-btn"
            onClick={handleSaveRiskRetention}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Ignition Gated Factors */}
      <div className="config-card">
        <div className="top-info">
          <div className="card-header">
            <div className="icon-wrapper" style={{ background: "#ECFDF5" }}>
              <ThunderboltOutlined style={{ color: "#10B981" }} />
            </div>
            <div className="header-text">
              <h3>Ignition Gated Factors Configuration</h3>
              <p>
                Select risk factors whose score should only count while the
                vehicle's ignition is on. Factors not selected here keep contributing to the score regardless of
                ignition state.
              </p>
            </div>
          </div>

          <Form form={ignitionGatedFactorsForm} layout="vertical">
            <div style={{ flex: 1 }}>
              <div className="input-label">Factors</div>
              <Form.Item name="factors">
                <Select
                  mode="multiple"
                  size="large"
                  placeholder="Select Risk Factors"
                  options={factors.map((factor) => ({
                    label: factor,
                    value: factor,
                  }))}
                />
              </Form.Item>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  marginTop: "8px",
                }}
              >
                {selectedIgnitionGatedFactors.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: "8px",
                    }}
                  >
                    {selectedIgnitionGatedFactors.map((factor) => (
                      <Tag
                        key={factor}
                        closable
                        onClose={() => {
                          ignitionGatedFactorsForm.setFieldValue(
                            "factors",
                            selectedIgnitionGatedFactors.filter(
                              (f) => f !== factor,
                            ),
                          );
                        }}
                      >
                        {factor}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Form>
        </div>

        <div className="save-btn-container">
          <Button
            className="custom-action-btn"
            onClick={handleSaveIgnitionGatedFactors}
          >
            Save
          </Button>
        </div>
      </div>

      {/* Geofences */}
      <div className="config-card">
        <div className="top-info" style={{ paddingBottom: "24px" }}>
          <div className="card-header">
            <div className="icon-wrapper" style={{ background: "#FEF2F2" }}>
              <SyncOutlined style={{ color: "#D80000" }} />
            </div>
            <div className="header-text">
              <h3>Geofence System Controls</h3>
              <p>Force update geographic boundaries for the entire fleet</p>
            </div>
          </div>

          <div className="sync-control-box">
            <div className="sync-text">
              <h4>Force sync all geofences across vehicles and devices</h4>
              <p>
                This action will immediately refresh all geofence data globally.
              </p>
            </div>
            <Button
              icon={<SyncOutlined />}
              size="large"
              className="sync-btn"
              onClick={handleSyncGeofence}
              loading={loading}
            >
              Force Geofence Sync
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationSettings;
